import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  Fade,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";

import {
  CalendarMonth,
  CheckCircleOutline,
  Close,
  CloudUploadOutlined,
  InfoOutlined,
  School,
  Unpublished,
  WarningAmberOutlined,
} from "@mui/icons-material";

import api from "../api/api";
import { useAcademicYear } from "../AcademicYearContext";

/* ============================================================
   DESIGN SYSTEM
============================================================ */

const COLORS = {
  deepNavy: "#071A2F",
  navy: "#0B2748",
  royalBlue: "#123F73",
  blue: "#1D5FA7",

  maroon: "#800000",
  deepMaroon: "#5C0000",

  gold: "#D6A928",
  brightGold: "#F2C94C",
  softGold: "#FFF8DE",

  white: "#FFFFFF",
  background: "#F4F7FB",
  surface: "#FFFFFF",

  text: "#172033",
  muted: "#667085",
  border: "#D9E2EC",

  success: "#087443",
  successSoft: "#ECFDF3",

  danger: "#B42318",
  dangerSoft: "#FFF1F0",

  warning: "#A15C00",
  warningSoft: "#FFF7E6",

  info: "#175CD3",
  infoSoft: "#EFF8FF",

  softBlue: "#F0F6FD",
};

/* ============================================================
   ERROR HANDLER
============================================================ */

const getErrorMessage = (error, fallback) => {
  const data = error?.response?.data;

  if (!data) {
    if (error?.code === "ECONNABORTED") {
      return "The request timed out. Please try again.";
    }

    return fallback;
  }

  if (typeof data === "string") return data;

  if (data.error) return data.error;
  if (data.message) return data.message;
  if (data.detail) return data.detail;

  if (
    Array.isArray(data.non_field_errors) &&
    data.non_field_errors.length
  ) {
    return data.non_field_errors[0];
  }

  const firstFieldError = Object.values(data).find(
    (value) => Array.isArray(value) && value.length > 0
  );

  return firstFieldError?.[0] || fallback;
};

/* ============================================================
   COMPONENT
============================================================ */

const AdminPublishGradesPopup = ({ open, onClose }) => {
  const { selectedYear } = useAcademicYear();

  /* ==========================================================
     STATE
  ========================================================== */

  const [periods, setPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState("");

  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [processingAction, setProcessingAction] = useState("");

  const [message, setMessage] = useState({
    type: "",
    text: "",
  });

  const messageTimerRef = useRef(null);
  const requestControllerRef = useRef(null);
  const processingRef = useRef(false);

  // Stable token reference.
  const token = useMemo(
    () => localStorage.getItem("access") || "",
    [open]
  );

  /* ==========================================================
     MESSAGE HANDLING
  ========================================================== */

  const clearMessage = useCallback(() => {
    setMessage({
      type: "",
      text: "",
    });

    if (messageTimerRef.current) {
      clearTimeout(messageTimerRef.current);
      messageTimerRef.current = null;
    }
  }, []);

  const showMessage = useCallback((type, text) => {
    if (messageTimerRef.current) {
      clearTimeout(messageTimerRef.current);
    }

    setMessage({
      type,
      text,
    });

    messageTimerRef.current = setTimeout(() => {
      setMessage({
        type: "",
        text: "",
      });

      messageTimerRef.current = null;
    }, 6000);
  }, []);

  /* ==========================================================
     CLEANUP
  ========================================================== */

  useEffect(() => {
    return () => {
      if (messageTimerRef.current) {
        clearTimeout(messageTimerRef.current);
      }

      requestControllerRef.current?.abort();
    };
  }, []);

  /* ==========================================================
     FETCH ACADEMIC PERIODS

     Fetch only when the dialog opens.
     Cancel outdated requests.
  ========================================================== */

  useEffect(() => {
    if (!open) return undefined;

    const controller = new AbortController();

    requestControllerRef.current?.abort();
    requestControllerRef.current = controller;

    let active = true;

    const fetchPeriods = async () => {
      setLoading(true);
      setPeriods([]);
      setSelectedPeriod("");
      clearMessage();

      try {
        const response = await api.get(
          "/api/users/periods/",
          {
            signal: controller.signal,
            headers: token
              ? {
                  Authorization: `Bearer ${token}`,
                }
              : {},
          }
        );

        if (!active) return;

        const data = response?.data;

        const fetchedPeriods = Array.isArray(data)
          ? data
          : Array.isArray(data?.results)
          ? data.results
          : [];

        setPeriods(fetchedPeriods);

        if (fetchedPeriods.length === 0) {
          showMessage(
            "warning",
            "No academic periods are currently available."
          );
        }
      } catch (error) {
        if (!active || error?.name === "CanceledError" ||
            error?.code === "ERR_CANCELED") {
          return;
        }

        setPeriods([]);

        showMessage(
          "error",
          getErrorMessage(
            error,
            "Failed to fetch academic periods. Please try again."
          )
        );
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    fetchPeriods();

    return () => {
      active = false;
      controller.abort();
    };
  }, [open, token, clearMessage, showMessage]);

  /* ==========================================================
     RESET WHEN CLOSED
  ========================================================== */

  useEffect(() => {
    if (!open) {
      setSelectedPeriod("");
      setPeriods([]);
      setLoading(false);

      processingRef.current = false;

      setProcessing(false);
      setProcessingAction("");

      clearMessage();
    }
  }, [open, clearMessage]);

  /* ==========================================================
     SELECTED PERIOD
  ========================================================== */

  const selectedPeriodObject = useMemo(
    () =>
      periods.find(
        (period) =>
          String(period.id) === String(selectedPeriod)
      ),
    [periods, selectedPeriod]
  );

  const hasAcademicYear = Boolean(selectedYear?.id);
  const hasPeriod = selectedPeriod !== "";

  const canProcess =
    hasAcademicYear &&
    hasPeriod &&
    !loading &&
    !processing;

  /* ==========================================================
     PERIOD CHANGE
  ========================================================== */

  const handlePeriodChange = useCallback(
    (event) => {
      setSelectedPeriod(event.target.value);
      clearMessage();
    },
    [clearMessage]
  );

  /* ==========================================================
     PUBLISH / UNPUBLISH
  ========================================================== */

  const processGrades = useCallback(
    async (action) => {
      if (processingRef.current) return;

      if (!selectedPeriod) {
        showMessage(
          "error",
          "Please select an academic period."
        );
        return;
      }

      if (!selectedYear?.id) {
        showMessage(
          "error",
          "No active academic year is selected."
        );
        return;
      }

      const isPublishing = action === "publish";

      const endpoint = isPublishing
        ? "/api/users/student-averages/publish-grades/"
        : "/api/users/student-averages/unpublish-grades/";

      processingRef.current = true;

      setProcessing(true);
      setProcessingAction(action);

      clearMessage();

      try {
        const response = await api.post(
          endpoint,
          {
            period: selectedPeriod,
            academic_year: selectedYear.id,
          },
          {
            headers: token
              ? {
                  Authorization: `Bearer ${token}`,
                }
              : {},
            timeout: 60000,
          }
        );

        showMessage(
          "success",
          response?.data?.message ||
            (isPublishing
              ? "Grades were published successfully."
              : "Grades were unpublished successfully.")
        );
      } catch (error) {
        showMessage(
          "error",
          getErrorMessage(
            error,
            isPublishing
              ? "Failed to publish grades."
              : "Failed to unpublish grades."
          )
        );
      } finally {
        processingRef.current = false;

        setProcessing(false);
        setProcessingAction("");
      }
    },
    [
      selectedPeriod,
      selectedYear,
      token,
      showMessage,
      clearMessage,
    ]
  );

  const publishGrades = useCallback(
    () => processGrades("publish"),
    [processGrades]
  );

  const unpublishGrades = useCallback(
    () => processGrades("unpublish"),
    [processGrades]
  );

  /* ==========================================================
     RENDER
  ========================================================== */

  return (
    <Dialog
      open={open}
      onClose={processing ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      TransitionComponent={Fade}
      keepMounted={false}
      disableRestoreFocus={false}
      PaperProps={{
        sx: {
          width: "100%",
          maxWidth: 620,
          margin: {
            xs: 1,
            sm: 2,
          },
          borderRadius: {
            xs: 3,
            sm: 4,
          },
          overflow: "visible",
          backgroundColor: COLORS.surface,
          border: `1px solid ${COLORS.border}`,
          boxShadow: "0 30px 80px rgba(7, 26, 47, 0.28)",
        },
      }}
      BackdropProps={{
        sx: {
          backgroundColor: "rgba(3, 16, 31, 0.72)",
        },
      }}
    >
      {/* HEADER */}

      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          borderRadius: {
            xs: "12px 12px 0 0",
            sm: "16px 16px 0 0",
          },
          background: `linear-gradient(135deg, ${COLORS.deepNavy}, ${COLORS.navy} 58%, ${COLORS.maroon} 150%)`,
          color: COLORS.white,
          px: {
            xs: 2.25,
            sm: 3.5,
          },
          py: {
            xs: 2.5,
            sm: 3,
          },
        }}
      >
        <Box
          sx={{
            position: "absolute",
            width: 180,
            height: 180,
            borderRadius: "50%",
            background: "rgba(242, 201, 76, 0.08)",
            top: -100,
            right: -50,
            pointerEvents: "none",
          }}
        />

        <IconButton
          onClick={onClose}
          disabled={processing}
          aria-label="Close grade publication dialog"
          sx={{
            position: "absolute",
            right: 10,
            top: 10,
            zIndex: 2,
            color: COLORS.white,
            backgroundColor: "rgba(255,255,255,0.08)",
            "&:hover": {
              backgroundColor: "rgba(255,255,255,0.16)",
            },
          }}
        >
          <Close />
        </IconButton>

        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          sx={{
            position: "relative",
            zIndex: 1,
            pr: 5,
          }}
        >
          <Box
            sx={{
              width: {
                xs: 52,
                sm: 60,
              },
              height: {
                xs: 52,
                sm: 60,
              },
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 2.5,
              backgroundColor: "rgba(255,255,255,0.11)",
              border: "1px solid rgba(242,201,76,0.42)",
            }}
          >
            <School
              sx={{
                fontSize: 32,
                color: COLORS.brightGold,
              }}
            />
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                color: COLORS.brightGold,
                mb: 0.4,
              }}
            >
              Academic Administration
            </Typography>

            <Typography
              sx={{
                fontSize: {
                  xs: 20,
                  sm: 25,
                },
                lineHeight: 1.2,
                fontWeight: 800,
              }}
            >
              Grade Publication Control
            </Typography>

            <Typography
              sx={{
                mt: 0.65,
                fontSize: 12.5,
                color: "rgba(255,255,255,0.75)",
              }}
            >
              Manage student academic result visibility.
            </Typography>
          </Box>
        </Stack>
      </Box>

      {/* CONTENT */}

      <DialogContent
        sx={{
          p: {
            xs: 2,
            sm: 3.5,
          },
          backgroundColor: COLORS.background,
          overflow: "visible",
        }}
      >
        {/* STATUS MESSAGE */}

        {message.text && (
          <Box sx={{ mb: 2.5 }}>
            <Alert
              severity={
                message.type === "success"
                  ? "success"
                  : message.type === "warning"
                  ? "warning"
                  : "error"
              }
              icon={
                message.type === "success" ? (
                  <CheckCircleOutline />
                ) : message.type === "warning" ? (
                  <WarningAmberOutlined />
                ) : (
                  <InfoOutlined />
                )
              }
              onClose={clearMessage}
              sx={{
                borderRadius: 2.5,
                fontSize: 13,
              }}
            >
              {message.text}
            </Alert>
          </Box>
        )}

        {/* ACADEMIC YEAR */}

        <Box
          sx={{
            border: `1px solid ${
              hasAcademicYear ? "#C8D9EA" : "#F0D6A5"
            }`,
            borderRadius: 3,
            backgroundColor: hasAcademicYear
              ? COLORS.white
              : COLORS.warningSoft,
            overflow: "hidden",
            mb: 2.5,
          }}
        >
          <Box
            sx={{
              height: 4,
              background: `linear-gradient(90deg, ${COLORS.blue}, ${COLORS.brightGold})`,
            }}
          />

          <Box
            sx={{
              p: 2.25,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                width: 42,
                height: 42,
                flexShrink: 0,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: COLORS.softGold,
                color: COLORS.maroon,
              }}
            >
              <CalendarMonth fontSize="small" />
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 800,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: COLORS.muted,
                  mb: 0.25,
                }}
              >
                Active Academic Year
              </Typography>

              <Typography
                sx={{
                  fontSize: 16,
                  fontWeight: 800,
                  color: COLORS.text,
                  wordBreak: "break-word",
                }}
              >
                {selectedYear?.name ||
                  "No academic year selected"}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* INSTRUCTION */}

        <Box sx={{ mb: 2.5 }}>
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 800,
              color: COLORS.text,
              mb: 0.55,
            }}
          >
            Select the reporting period
          </Typography>

          <Typography
            sx={{
              fontSize: 12.5,
              lineHeight: 1.6,
              color: COLORS.muted,
            }}
          >
            Choose the period whose student grades should be made visible or
            hidden from authorized users.
          </Typography>
        </Box>

        {/* PERIOD SELECT */}

        <FormControl
          fullWidth
          disabled={loading || processing}
          variant="outlined"
        >
          <InputLabel
            id="grade-publication-period-label"
            shrink
          >
            Academic Period
          </InputLabel>

          <Select
            labelId="grade-publication-period-label"
            id="grade-publication-period"
            value={selectedPeriod}
            label="Academic Period"
            onChange={handlePeriodChange}
            displayEmpty
            MenuProps={{
              disablePortal: true,
              disableScrollLock: true,
              keepMounted: false,
              transitionDuration: 100,
              anchorOrigin: {
                vertical: "bottom",
                horizontal: "left",
              },
              transformOrigin: {
                vertical: "top",
                horizontal: "left",
              },
              PaperProps: {
                elevation: 8,
                sx: {
                  mt: 0.75,
                  maxHeight: 280,
                  borderRadius: 2,
                  border: `1px solid ${COLORS.border}`,
                  backgroundColor: COLORS.white,
                  boxShadow: "0 14px 40px rgba(7, 26, 47, 0.18)",
                  zIndex: 1501,
                  "& .MuiMenuItem-root": {
                    minHeight: 46,
                    whiteSpace: "normal",
                  },
                },
              },
            }}
            sx={{
              backgroundColor: COLORS.white,
              borderRadius: 2.5,
              minHeight: 56,

              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: COLORS.border,
              },

              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: COLORS.blue,
              },

              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: COLORS.maroon,
                borderWidth: 2,
              },
            }}
          >
            {loading ? (
              <MenuItem disabled value="">
                <Stack
                  direction="row"
                  spacing={1.25}
                  alignItems="center"
                >
                  <CircularProgress
                    size={18}
                    sx={{ color: COLORS.maroon }}
                  />

                  <Typography sx={{ fontSize: 13 }}>
                    Loading academic periods...
                  </Typography>
                </Stack>
              </MenuItem>
            ) : periods.length > 0 ? (
              periods.map((period) => (
                <MenuItem
                  key={period.id}
                  value={String(period.id)}
                  sx={{
                    py: 1.25,
                    fontSize: 14,
                    fontWeight: 600,
                  }}
                >
                  {period.name}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled value="">
                <Typography
                  sx={{
                    fontSize: 13,
                    color: COLORS.muted,
                  }}
                >
                  No academic periods found
                </Typography>
              </MenuItem>
            )}
          </Select>
        </FormControl>

        {/* SELECTED PERIOD PREVIEW */}

        {selectedPeriodObject && (
          <Box
            sx={{
              mt: 1.5,
              px: 1.75,
              py: 1.25,
              borderRadius: 2,
              backgroundColor: COLORS.softBlue,
              border: "1px solid #CFE0F2",
              display: "flex",
              alignItems: "center",
              gap: 1.25,
            }}
          >
            <CheckCircleOutline
              sx={{
                color: COLORS.success,
                fontSize: 19,
              }}
            />

            <Typography
              sx={{
                fontSize: 12.5,
                color: COLORS.royalBlue,
              }}
            >
              Selected period:{" "}
              <Box
                component="span"
                sx={{ fontWeight: 800 }}
              >
                {selectedPeriodObject.name}
              </Box>
            </Typography>
          </Box>
        )}

        <Divider
          sx={{
            my: 3,
            borderColor: COLORS.border,
          }}
        />

        {/* PUBLICATION ACTION */}

        <Box>
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 800,
              color: COLORS.text,
              mb: 0.55,
            }}
          >
            Publication action
          </Typography>

          <Typography
            sx={{
              fontSize: 12.5,
              lineHeight: 1.6,
              color: COLORS.muted,
              mb: 2,
            }}
          >
            Publishing makes the selected period's grades available according
            to your application's grade visibility rules. Unpublishing
            reverses that publication state.
          </Typography>

          <Stack
            direction={{
              xs: "column",
              sm: "row",
            }}
            spacing={1.5}
          >
            <Button
              fullWidth
              variant="contained"
              disableElevation
              startIcon={
                processingAction === "publish" ? (
                  <CircularProgress
                    size={19}
                    color="inherit"
                  />
                ) : (
                  <CloudUploadOutlined />
                )
              }
              onClick={publishGrades}
              disabled={!canProcess}
              sx={{
                minHeight: 52,
                borderRadius: 2.5,
                textTransform: "none",
                fontSize: 14,
                fontWeight: 800,
                background: `linear-gradient(135deg, ${COLORS.maroon}, ${COLORS.deepMaroon})`,
                "&:hover": {
                  background: `linear-gradient(135deg, #970000, ${COLORS.maroon})`,
                },
                "&:disabled": {
                  background: "#D6D9DE",
                  color: "#8A9099",
                },
              }}
            >
              {processingAction === "publish"
                ? "Publishing..."
                : "Publish Grades"}
            </Button>

            <Button
              fullWidth
              variant="outlined"
              startIcon={
                processingAction === "unpublish" ? (
                  <CircularProgress
                    size={19}
                    color="inherit"
                  />
                ) : (
                  <Unpublished />
                )
              }
              onClick={unpublishGrades}
              disabled={!canProcess}
              sx={{
                minHeight: 52,
                borderRadius: 2.5,
                textTransform: "none",
                fontSize: 14,
                fontWeight: 800,
                color: COLORS.deepNavy,
                borderColor: "#B8C4D1",
                backgroundColor: COLORS.white,
                "&:hover": {
                  borderColor: COLORS.deepNavy,
                  backgroundColor: "#F3F6F9",
                },
                "&:disabled": {
                  borderColor: "#D6D9DE",
                  color: "#9AA1AA",
                },
              }}
            >
              {processingAction === "unpublish"
                ? "Unpublishing..."
                : "Unpublish Grades"}
            </Button>
          </Stack>
        </Box>

        {/* ADMIN NOTICE */}

        <Box
          sx={{
            mt: 2.5,
            p: 1.75,
            borderRadius: 2.5,
            backgroundColor: "#F8FAFC",
            border: `1px solid ${COLORS.border}`,
            display: "flex",
            gap: 1.25,
            alignItems: "flex-start",
          }}
        >
          <InfoOutlined
            sx={{
              color: COLORS.blue,
              fontSize: 19,
              mt: 0.1,
              flexShrink: 0,
            }}
          />

          <Box>
            <Typography
              sx={{
                fontSize: 12.5,
                fontWeight: 800,
                color: COLORS.text,
                mb: 0.35,
              }}
            >
              Administrative control
            </Typography>

            <Typography
              sx={{
                fontSize: 12,
                lineHeight: 1.6,
                color: COLORS.muted,
              }}
            >
              Confirm the academic year and period carefully before changing
              publication status. The selected period is processed using the
              academic year currently active in the system.
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      {/* PROCESSING OVERLAY */}

      {processing && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 20,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255,255,255,0.42)",
            backdropFilter: "blur(1.5px)",
            pointerEvents: "none",
          }}
        >
          <Box
            sx={{
              px: 2.5,
              py: 1.75,
              borderRadius: 3,
              backgroundColor: "rgba(7,26,47,0.94)",
              color: COLORS.white,
              boxShadow: "0 18px 45px rgba(0,0,0,0.24)",
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <CircularProgress
              size={22}
              thickness={4}
              sx={{
                color: COLORS.brightGold,
              }}
            />

            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 700,
              }}
            >
              {processingAction === "publish"
                ? "Publishing grades..."
                : "Unpublishing grades..."}
            </Typography>
          </Box>
        </Box>
      )}
    </Dialog>
  );
};

export default AdminPublishGradesPopup;
